import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaEnvelope } from 'react-icons/fa';
import Section from '../common/Section';
import Button from '../common/Button';

const ContactContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled(motion.div)`
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    order: 2;
  }
`;

const ContactForm = styled(motion.div)`
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    order: 1;
  }
`;

const SectionTitle = styled.h2`
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const SectionDescription = styled.p`
  color: ${({ theme }) => theme.colors.grey[700]};
  margin-bottom: ${({ theme }) => theme.space.xl};
  max-width: 500px;
`;

const ContactCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
`;

const ContactCard = styled.div`
  padding: ${({ theme }) => theme.space.lg};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  display: flex;
  gap: ${({ theme }) => theme.space.md};
  transition: all ${({ theme }) => theme.transitions.normal};
  border-left: 4px solid transparent;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-left: 4px solid ${({ theme }) => theme.colors.accent};
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: ${({ theme }) => theme.borderRadius.blob};
  background: ${({ theme }) => theme.colors.gradient.neon};
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  flex-shrink: 0;
  box-shadow: ${({ theme }) => theme.shadows.neon};
  animation: ${({ theme }) => theme.animations.pulse};
`;

const ContactCardContent = styled.div`
  h4 {
    margin-bottom: ${({ theme }) => theme.space.xs};
  }
  
  p {
    color: ${({ theme }) => theme.colors.grey[700]};
    margin-bottom: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.space.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.space.md};
  border: 1px solid ${({ theme }) => theme.colors.grey[300]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: ${({ theme }) => theme.fonts.body};
  transition: all ${({ theme }) => theme.transitions.normal};
  background-color: ${({ theme }) => theme.colors.grey[100]};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: ${({ theme }) => theme.shadows.neon};
    transform: translateY(-2px);
  }
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.space.md};
  border: 1px solid ${({ theme }) => theme.colors.grey[300]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: ${({ theme }) => theme.fonts.body};
  resize: vertical;
  min-height: 150px;
  transition: all ${({ theme }) => theme.transitions.normal};
  background-color: ${({ theme }) => theme.colors.grey[100]};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: ${({ theme }) => theme.shadows.neon};
    transform: translateY(-2px);
  }
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ContactSection: React.FC = () => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("This is a static form. Feature coming soon!");
  };
  
  return (
    <Section id="contact">
      <ContactContainer>
        <ContactInfo
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ type: 'spring', stiffness: 100, damping: 15 }}
        >
          <SectionTitle>Get In Touch</SectionTitle>
          <SectionDescription>
            Have a question or ready to start your digital transformation journey? Reach out to us through any of these channels or use the form.
          </SectionDescription>
          
          <ContactCards>
            <ContactCard>
              <IconWrapper>
                <FaMapMarkerAlt />
              </IconWrapper>
              <ContactCardContent>
                <h4>Our Location</h4>
                <p>Saguna More, Patna, Bihar</p>
                <iframe src="https://www.google.com/maps?q=Saguna+More,+Patna,+Bihar&output=embed" width="100%" height="200" frameBorder="0" style={{border:0}}></iframe>
              </ContactCardContent>
            </ContactCard>
            
            <ContactCard>
              <IconWrapper>
                <FaPhone />
              </IconWrapper>
              <ContactCardContent>
                <h4>Phone Number</h4>
                <p>6203306234</p>
              </ContactCardContent>
            </ContactCard>
            
            <ContactCard>
              <IconWrapper>
                <FaEnvelope />
              </IconWrapper>
              <ContactCardContent>
                <h4>Email Address</h4>
                <p>connect@datadigital.in</p>
              </ContactCardContent>
            </ContactCard>
          </ContactCards>
        </ContactInfo>
        
        <ContactForm
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ type: 'spring', stiffness: 100, damping: 15, delay: 0.2 }}
        >
          <form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label htmlFor="name">Your Name</Label>
                <Input type="text" id="name" placeholder="John Doe" />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input type="email" id="email" placeholder="connect@datadigital.in" />
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <Label htmlFor="subject">Subject</Label>
              <Input type="text" id="subject" placeholder="How can we help you?" />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="message">Your Message</Label>
              <TextArea id="message" placeholder="Tell us about your project..." />
            </FormGroup>
            
            <Button variant="primary" size="large" type="submit">
              Send Message
            </Button>
          </form>
        </ContactForm>
      </ContactContainer>
    </Section>
  );
};

export default ContactSection;
